/**
 * Client Configuration
 * Centralized configuration values for the client application
 *
 * Shared validation constants (age, height, score, text limits, media, matchmaker)
 * are defined in @couple/shared-options and re-exported here so existing import
 * paths continue to work.
 */

// Re-export shared validation constants
export {
  MIN_AGE,
  MAX_AGE,
  MIN_HEIGHT_CM,
  MAX_HEIGHT_CM,
  MIN_SCORE,
  MAX_SCORE,
  DEFAULT_MIN_AGE,
  DEFAULT_MAX_AGE,
  MAX_HEADLINE_LENGTH,
  MAX_BIO_LENGTH,
  AI_MATCHMAKER_USER_ID,
} from '@couple/shared-options';

// Re-export with client-side names
export { MAX_PROFILE_PHOTOS as MAX_PHOTOS } from '@couple/shared-options';
export { MAX_UPLOAD_FILE_SIZE_BYTES as MAX_FILE_SIZE_BYTES } from '@couple/shared-options';

// =============================================================================
// Platform & Environment Detection
// =============================================================================

// Check if running in Capacitor native app
function isNativePlatform(): boolean {
  return !!(window as any).Capacitor?.isNativePlatform?.();
}

// Environment detection for web
function getEnvironment(): 'native' | 'development' | 'production' {
  if (isNativePlatform()) return 'native';
  const hostname = window.location.hostname;
  const isDev = hostname === 'localhost' || hostname === '127.0.0.1'
    || hostname.startsWith('192.168.') || hostname === 'dev.22questions.com';
  return isDev ? 'development' : 'production';
}

// =============================================================================
// API & Socket Configuration
// =============================================================================
const PROD_SERVER = 'https://api.22questions.com';

function getApiBaseUrl(): string {
  const env = getEnvironment();
  // Native apps always use production API
  if (env === 'native') return PROD_SERVER;
  // Dev subdomain uses its own port (Docker host mapping)
  if (window.location.hostname === 'dev.22questions.com') return `http://dev.22questions.com:3022`;
  // Web: dev server on same host (works with localhost and LAN IPs)
  if (env === 'development') return `http://${window.location.hostname}:3000`;
  return PROD_SERVER;
}

export const API_BASE_URL = getApiBaseUrl();
export const SOCKET_URL = getApiBaseUrl();

function isIOSNative(): boolean {
  return isNativePlatform() && (window as any).Capacitor?.getPlatform?.() === 'ios';
}

// Export for use in other modules
export { isNativePlatform, isIOSNative, getEnvironment };

// =============================================================================
// Timing & Delays (in milliseconds)
// =============================================================================
export const AUTO_SAVE_DEBOUNCE_MS = 400;
export const SAVE_INDICATOR_DURATION_MS = 650;
export const TOAST_DISPLAY_DURATION_MS = 1200;
export const TIME_UPDATE_INTERVAL_MS = 30000;
export const TYPING_TIMEOUT_MS = 10000;

// =============================================================================
// Age (client-only)
// =============================================================================
export const MAX_VALID_AGE = 120;

// =============================================================================
// Height (client-only)
// =============================================================================
export const CM_TO_FEET_DIVISOR = 30.48;
export const CM_TO_INCHES_DIVISOR = 2.54;

// =============================================================================
// Interaction Scoring (client-only)
// =============================================================================
export const SKIP_SCORE = -10;

// =============================================================================
// Colors
// =============================================================================
export const POSITIVE_SCORE_COLOR = '#4caf50';
export const NEGATIVE_SCORE_COLOR = '#f44336';

// =============================================================================
// Text Limits (client-only)
// =============================================================================
export const MESSAGE_PREVIEW_MAX_LENGTH = 60;

// =============================================================================
// Branding
// =============================================================================
export const APP_TITLE = '22 Questions';
export const APP_TAGLINE = 'Get To Know Me';
